import {
  useCallback, useEffect, useState, useRef,
} from 'react';
import { animated, useSpring } from 'react-spring';
import { toast } from 'react-toastify';
import styles from './docDetailModalFrame.module.css';
import { Document, DocumentDetail } from '../../services/http/documents.api';
// eslint-disable-next-line import/no-cycle
import DocDetailInfo from './docDetailInfo';
import DocDetailHistory from './docDetailHistory';
import { useDocumentGetDetail } from '../../hooks/api/document.hook';
import { ApiError } from '../../services/http';
import { useMessageModal } from '../../hooks/modal.hook';
import DocDetailHeader from './docDetailHeader';
import routes from '../../utils/routes';

/**
 * 文書詳細モーダルフレームのプロップスインターフェース
 */
export interface Props {
  /** 使用される文書の参照 */
  document: Document | null;
  /** モーダルが閉じられるときに呼び出される関数 */
  onClose: () => void
  /** 文書が削除されるときに呼び出される関数 */
  onRemoveDocument: (document: DocumentDetail) => void,
  /** 文書が更新されるときに呼び出される関数 */
  onDocumentUpdated: () => void

  onCancelAddingRelatedDocuments?: () => void;

  onRemoveDocumentVersion?: (version: DocumentDetail['documents'][number]) => void;
  /** 関連文書の追加をキャンセルする関数 */
  highlightTexts?: string[];
  /** ハイライトするテキストの配列 */
  searchKeyword?: string;
}

/**
 * 文書詳細モーダルフレーム
 */
function DocDetailModalFrame({
  document: documentRef, onRemoveDocument, onClose, onDocumentUpdated, onCancelAddingRelatedDocuments = () => {},
  onRemoveDocumentVersion = () => {}, highlightTexts = [], searchKeyword,
}: Props) {
  // States
  const [document, setDocument] = useState<DocumentDetail | null>(null);
  const [isClosed, setIsClosed] = useState(false);
  const [modalClosed, setModalClosed] = useState(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState(0);

  // Refs
  const latestRequestedDocumentId = useRef<number | null>(null);

  // Hooks
  const documentGetDetail = useDocumentGetDetail();
  const openMessageModal = useMessageModal();

  // Methods
  const onDocUpdated = useCallback(() => {
    if (documentRef) {
      try {
        latestRequestedDocumentId.current = documentRef.id;
        documentGetDetail.request({ id: documentRef.id }).then((detail) => {
          if (latestRequestedDocumentId.current === documentRef.id) {
            setDocument(detail);
          }
        });
      } catch (e) {
        openMessageModal((e as ApiError)?.message);
      }
    }
  }, [documentGetDetail, documentRef, openMessageModal]);

  const onRemoveVersion = useCallback((version: DocumentDetail['documents'][number]) => {
    if (!document) return;
    setDocument({ ...document, documents: document.documents.filter((item) => item !== version) });
    onDocUpdated();
    onRemoveDocumentVersion(version);
  }, [document, onDocUpdated]);

  const onDocDeleted = useCallback((doc: DocumentDetail) => {
    onRemoveDocument(doc);
    onClose();
  }, [onClose, onRemoveDocument]);

  const spring = useSpring({
    left: (documentRef && !isClosed) ? window.innerWidth - 877 : window.innerWidth + 200,
    display: documentRef ? 'block' : 'none',
    maxWidth: '877px',
    zIndex: 100,
  });

  const onCloseModal = useCallback(() => {
    setIsEditing(false);
    setIsClosed(true);
    onClose();
    setModalClosed(true);
  }, [onClose]);

  const handleClickShareLink = () => {
    const link = `${window.location.origin}${routes.main}?documentId=${documentRef?.ccControlNumber}`;
    navigator.clipboard.writeText(link);
    toast('共有リンクをクリップボードにコピーしました', { style: { width: 380 } });
  };

  // Effects
  useEffect(() => {
    if (documentRef) {
      const currentDocId = documentRef.id;
      latestRequestedDocumentId.current = currentDocId;

      try {
        documentGetDetail.request({ id: currentDocId }).then((detail) => {
          if (latestRequestedDocumentId.current === currentDocId && detail.id === currentDocId) {
            setDocument(detail);
          }
        });
      } catch (e) {
        openMessageModal((e as ApiError)?.message);
      }
    }
  }, [documentRef?.id]);

  useEffect(() => {
    if (!documentRef) setIsClosed(false);
  }, [documentRef, onClose]);

  return (

    <div style={isEditing ? {
      position: documentRef ? 'fixed' : 'unset',
      background: documentRef ? 'rgba(0, 0, 0, 0.5)' : '',
      zIndex: documentRef ? 99 : '',
      width: documentRef ? '100%' : '',
      height: documentRef ? '100%' : '',
      top: documentRef ? '0' : '',
      left: documentRef ? '0' : '',
    } : {}}
    >
      <animated.div id="modal" className={styles.bodyBackground} style={spring}>

        <div className={styles.modalContainer}>
          <div className={styles.modal}>
            <DocDetailHeader activeTab={activeTab} setActiveTag={setActiveTab} onClickShareLink={handleClickShareLink} onClose={onCloseModal} />
            <div style={{ height: 'calc(100% - 74px)' }}>
              {
                activeTab === 0 && (
                  <DocDetailInfo
                    document={document}
                    onClose={onCloseModal}
                    onDocumentUpdated={onDocumentUpdated}
                    onRemoveDocumentVersion={onRemoveVersion}
                    onUpdateVersion={onDocUpdated}
                    onRemoveDocument={onDocDeleted}
                    setIsEditing={setIsEditing}
                    onCancelAddingRelatedDocuments={onCancelAddingRelatedDocuments}
                    highlightTexts={highlightTexts}
                    searchKeyword={searchKeyword}
                  />
                )
              }
              {
                activeTab === 1 && (
                  <DocDetailHistory
                    document={document}
                    onClose={() => {
                      setModalClosed(true);
                      setIsClosed(true);
                      onClose();
                    }}
                    setIsEditing={setIsEditing}
                  />
                )
              }
            </div>
          </div>
        </div>
      </animated.div>
    </div>
  );
}

export default DocDetailModalFrame;
